// Mineral-pool salt + magnesium auto-dosing.
// Two options are always produced so staff/clients choose on outcome, not chemistry:
//   A) Balanced  = Dead Sea Mineral Salt (covers the magnesium top-up) + Pool Salt (covers the rest)
//   B) Best value = Pool Salt (covers all the salt) + Lo-Chlor Therapy (tops the minerals only)

export function parseBand(s) {
  if (!s) return null;
  const nums = (String(s).match(/\d+/g) || []).map(Number);
  if (nums.length === 0) return null;
  if (nums.length === 1) return [nums[0], nums[0]];
  return [nums[0], nums[1]];
}

const targetOf = (band, mode) => (band ? (mode === "min" ? band[0] : Math.round((band[0] + band[1]) / 2)) : null);
const money = (n) => Math.round(n * 100) / 100;

function line(p, qty, unit) {
  const price = Number(p.price) || 0;
  return { name: p.name, part_id: p.part_id, qty, unit: unit || "bag", price, total: money(qty * price) };
}
function buildOption(label, rawLines) {
  const lines = rawLines.filter(Boolean);
  return { label, lines, total: money(lines.reduce((s, l) => s + l.total, 0)) };
}

export function isMineralBody(body) {
  const t = `${body?.type || ""} ${body?.category || ""}`.toLowerCase();
  return t.includes("mineral") || t.includes("magna");
}

// readings: {salt, magnesium}; body carries salt_level + magnesium_level bands + volume_litres
export function computeMineralDose({ readings, body, rules }) {
  if (!rules || !body) return { applicable: false };
  const V = Number(body.volume_litres) || 0;
  const saltBand = parseBand(body.salt_level);
  const magBand = parseBand(body.magnesium_level);
  if (!V || !magBand) return { applicable: false };

  const saltReading = Number(readings?.salt) || 0;
  const magReading = Number(readings?.magnesium) || 0;
  const saltT = targetOf(saltBand, rules.salt_target);
  const magT = targetOf(magBand, rules.mineral_target);

  const dS = saltBand ? Math.max(0, saltT - saltReading) : 0;   // ppm salt to add
  const dM = Math.max(0, magT - magReading);                    // ppm minerals to add
  if (dS <= 0 && dM <= 0) return { applicable: false, inRange: true };

  const ps = rules.pool_salt || {}, ms = rules.mineral_salt || {}, th = rules.therapy || {};
  const saltKgNeeded = (dS * V) / 1e6; // pure NaCl to lift dS ppm in V litres
  const mgPerMineralBag = (Number(ms.mg_ppm_per_bag_50kL) || 125) * (50000 / V);
  const mgPerTherapy = (Number(th.mg_ppm_per_bottle_50kL) || 200) * (50000 / V);

  // Option A — Balanced: just enough mineral salt for the magnesium, cheap salt for the rest
  const mineralBags = dM > 0 ? Math.max(1, Math.ceil(dM / mgPerMineralBag)) : 0;
  const mineralSaltKg = mineralBags * (Number(ms.bag_kg) || 10);
  const remKg = Math.max(0, saltKgNeeded - mineralSaltKg);
  const normalBagsA = Math.max(0, Math.ceil(remKg / (Number(ps.bag_kg) || 20)));
  // Option 0 — All Dead Sea Mineral: every kg of salt needed comes from mineral salt bags
  const allMineralBags = Math.max(mineralBags, Math.ceil(saltKgNeeded / (Number(ms.bag_kg) || 10)));
  const optAll = buildOption("All Dead Sea Mineral", [allMineralBags ? line(ms, allMineralBags) : null]);
  const optA = buildOption("Dead Sea Mineral + Pool Salt", [
    mineralBags ? line(ms, mineralBags) : null,
    normalBagsA ? line(ps, normalBagsA) : null,
  ]);

  // Option B — Best value: all salt from cheap Pool Salt + liquid minerals for the top-up
  const normalBagsB = Math.max(0, Math.ceil(saltKgNeeded / (Number(ps.bag_kg) || 20)));
  const therapyBottles = dM > 0 ? Math.max(1, Math.round(dM / mgPerTherapy)) : 0;
  const optB = buildOption("Pool Salt + Liquid Magnesium", [
    normalBagsB ? line(ps, normalBagsB) : null,
    therapyBottles ? line(th, therapyBottles, "bottle") : null,
  ]);

  return {
    applicable: true,
    saltReading, magReading, saltBand, magBand, saltTarget: saltT, magTarget: magT,
    options: [optAll, optA, optB].filter((o, i, all) => o.lines.length && all.findIndex((x) => JSON.stringify(x.lines) === JSON.stringify(o.lines)) === i),
  };
}
